import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  TextField
} from '@mui/material';
import { useAppSelector } from '../../store';
import {
  calculateFreightRev,
  calculateFuelSurcharge,
  calculateDemurrageCharge,
  calculateTotalCharges
} from '../../config/calculations';
import { PendingInvoice as Invoice } from './types';

interface EditDialogProps {
  open: boolean;
  invoice: Invoice | null;
  onClose: () => void;
  onSave: (updatedInvoice: Invoice) => Promise<void>;
}

const terminals = [
  'Tesoro',
  'Kinder Morgan',
  'Chevron',
] as const;

const EditDialog: React.FC<EditDialogProps> = ({ open, invoice, onClose, onSave }) => {
  const [formData, setFormData] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manualTotalCharges, setManualTotalCharges] = useState(false);
  const { items: brokers } = useAppSelector((state) => state.brokers);
  const { user } = useAppSelector((state) => state.auth);

  // Format number for display
  const formatNumber = (value: number | string | undefined): string => {
    if (typeof value === 'number') {
      return value.toFixed(2);
    }
    if (typeof value === 'string') {
      const num = parseFloat(value);
      return isNaN(num) ? '0.00' : num.toFixed(2);
    }
    return '0.00';
  };

  useEffect(() => {
    setFormData(invoice);
    setManualTotalCharges(false);
  }, [invoice]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    if (!formData) return;
    
    let value: string | number = e.target.value;
    const name = e.target.name;
    
    // Handle manual total charges editing
    if (name === 'totalCharges' && manualTotalCharges) {
      // Remove any non-numeric characters except decimal point
      const cleanValue = value.replace(/[^0-9.]/g, '');
      // Parse as float, default to 0 if invalid
      const numericValue = parseFloat(cleanValue) || 0;
      setFormData({
        ...formData,
        [name]: numericValue,
        updatedAt: new Date()
      });
      return;
    }
    
    // Convert numeric fields to numbers
    if (['gross', 'rate', 'net', 'fuelSurchargePercent', 'totalDem'].includes(name)) {
      value = parseFloat(value) || 0;
      
      // Calculate derived values
      const gross = name === 'gross' ? value : formData.gross || 0;
      const rate = name === 'rate' ? value : formData.rate || 0;
      const net = name === 'net' ? value : formData.net || 0;
      const fuelSurchargePercent = name === 'fuelSurchargePercent' ? value : formData.fuelSurchargePercent || 0;
      const totalDem = name === 'totalDem' ? value : formData.totalDem || 0;

      const calculatedFreightRev = calculateFreightRev(gross, rate);
      const calculatedFuelSurcharge = calculateFuelSurcharge(calculatedFreightRev, fuelSurchargePercent);
      const calculatedDemurrageCharge = calculateDemurrageCharge(totalDem);
      const calculatedTotalCharges = calculateTotalCharges(calculatedFreightRev, calculatedFuelSurcharge, calculatedDemurrageCharge);

      setFormData({
        ...formData,
        [name]: value,
        freightRev: calculatedFreightRev,
        fuelSurcharge: calculatedFuelSurcharge,
        demurrageCharge: calculatedDemurrageCharge,
        totalCharges: manualTotalCharges ? formData.totalCharges : calculatedTotalCharges,
        updatedAt: new Date()
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
        updatedAt: new Date()
      });
    }
  };

  const handleSubmit = async () => {
    if (!formData) return;
    setLoading(true);
    setError(null);
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while saving');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Invoice</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box component="form" sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {/* Basic Invoice Information */}
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
              <TextField
                fullWidth
                label="Invoice Number"
                name="invoiceNumber"
                value={formData?.invoiceNumber || ''}
                onChange={handleChange}
                disabled
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
              <TextField
                fullWidth
                label="Order Number"
                name="orderNumber"
                value={formData?.orderNumber || ''}
                onChange={handleChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
              <TextField
                fullWidth
                label="Load Number"
                name="loadNumber"
                value={formData?.loadNumber || ''}
                onChange={handleChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
              <FormControl fullWidth>
                <InputLabel>Broker</InputLabel>
                <Select
                  name="broker"
                  value={formData?.broker || ''}
                  onChange={handleChange}
                  label="Broker"
                >
                  {brokers.map((broker) => (
                    <MenuItem key={broker.id} value={broker.shortname}>
                      {broker.shortname}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Location Information */}
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
              <FormControl fullWidth>
                <InputLabel>Terminal</InputLabel>
                <Select
                  name="terminal"
                  value={formData?.terminal || ''}
                  onChange={handleChange}
                  label="Terminal"
                >
                  {terminals.map((terminal: string) => (
                    <MenuItem key={terminal} value={terminal}>{terminal}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
              <TextField
                fullWidth
                label="Destination"
                name="destination"
                value={formData?.destination || ''}
                onChange={handleChange}
              />
            </Box>

            {/* Financial Information */}
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
              <TextField
                fullWidth
                type="number"
                label="Gross"
                name="gross"
                value={formData?.gross || ''}
                onChange={handleChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
              <TextField
                fullWidth
                type="number"
                label="Net"
                name="net"
                value={formData?.net || ''}
                onChange={handleChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
              <TextField
                fullWidth
                type="number"
                label="Rate"
                name="rate"
                value={formData?.rate || ''}
                onChange={handleChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
              <TextField
                fullWidth
                type="number"
                label="Fuel Surcharge %"
                name="fuelSurchargePercent"
                value={formData?.fuelSurchargePercent || ''}
                onChange={handleChange}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
              <TextField
                fullWidth
                type="number"
                label="Total Demurrage"
                name="totalDem"
                value={formData?.totalDem || ''}
                onChange={handleChange}
              />
            </Box>

            {/* Calculated Fields (Read-only) */}
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
              <TextField
                fullWidth
                type="number"
                label="Freight Revenue"
                name="freightRev"
                value={formatNumber(formData?.freightRev)}
                InputProps={{ readOnly: true }}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
              <TextField
                fullWidth
                type="number"
                label="Fuel Surcharge (Total)"
                name="fuelSurcharge"
                value={formatNumber(formData?.fuelSurcharge)}
                InputProps={{ readOnly: true }}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
              <TextField
                fullWidth
                type="number"
                label="Demurrage Charge"
                name="demurrageCharge"
                value={formatNumber(formData?.demurrageCharge)}
                InputProps={{ readOnly: true }}
              />
            </Box>
            
            {/* Total Charges Field - Editable for Admin Users */}
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
              {user?.accountType === 'admin' && (
                <Box sx={{ mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={manualTotalCharges}
                        onChange={(e) => setManualTotalCharges(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Manual Override"
                  />
                </Box>
              )}
              <TextField
                fullWidth
                type={manualTotalCharges && user?.accountType === 'admin' ? 'text' : 'number'}
                label="Total Charges"
                name="totalCharges"
                value={formatNumber(formData?.totalCharges)}
                onChange={handleChange}
                InputProps={{ 
                  readOnly: !manualTotalCharges || user?.accountType !== 'admin',
                  sx: manualTotalCharges && user?.accountType === 'admin' ? { 
                    backgroundColor: 'action.hover',
                    '&:hover': { backgroundColor: 'action.selected' }
                  } : {}
                }}
                helperText={manualTotalCharges && user?.accountType === 'admin' ? 
                  'Manual override enabled - calculated value will be ignored' : 
                  'Calculated automatically from other fields'
                }
              />
            </Box>

            {/* Driver Field - Editable for Admin Users */}
            {user?.accountType === 'admin' && (
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
                <TextField
                  fullWidth
                  label="Driver"
                  name="driver"
                  value={formData?.driver || ''}
                  onChange={handleChange}
                  placeholder="Enter driver dogtag"
                />
              </Box>
            )}

            {/* Additional Fields */}
            <Box sx={{ flex: '1 1 100%' }}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData?.notes || ''}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDialog;
